/**
 * Access Requests Panel Component
 * 
 * Panel for owners to view and respond to access requests
 * Shows pending requests with approve/deny buttons
 */

import React, { useState, useEffect } from 'react';
import { 
  getOwnerPendingRequests, 
  respondToAccessRequest,
  getOwnerRequestStats 
} from '../../utils/accessRequestHelpers';
import './AccessRequestsPanel.css';

const AccessRequestsPanel = ({ user, showAll = false }) => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [error, setError] = useState('');

  // Fetch pending requests
  useEffect(() => {
    if (!user) return;

    const fetchRequests = async () => {
      try {
        const pendingRequests = await getOwnerPendingRequests(user.uid);
        setRequests(pendingRequests);
        setError(''); // Clear any previous errors
        
        // Get stats (optional - may fail if index not ready)
        try {
          const requestStats = await getOwnerRequestStats(user.uid);
          setStats(requestStats);
        } catch (statsErr) {
          // Stats are optional - don't show error if this fails
          console.log('Stats not available yet (index building)');
        }
      } catch (err) {
        console.error('Error fetching requests:', err);
        // Only show error if we actually failed to get requests
        if (!requests || requests.length === 0) {
          setError('Failed to load requests');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();

    // Refresh every 30 seconds
    const interval = setInterval(fetchRequests, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleRespond = async (requestId, action) => {
    setResponding(requestId);
    setError('');

    try {
      await respondToAccessRequest(
        requestId,
        action,
        user.uid,
        responseMessage.trim()
      );

      // Remove from list
      setRequests(prev => prev.filter(r => r.id !== requestId));
      
      // Close modal
      setShowResponseModal(null);
      setResponseMessage('');
      
      // Show success message
      const actionText = action === 'approve' ? 'approved' : 'denied';
      alert(`Request ${actionText} successfully!`);

      // Update stats
      const requestStats = await getOwnerRequestStats(user.uid);
      setStats(requestStats);

    } catch (err) {
      console.error('Error responding to request:', err);
      setError(err.message || 'Failed to respond to request');
    } finally {
      setResponding(null);
    }
  };

  const openResponseModal = (request, action) => {
    setShowResponseModal({ request, action });
    setResponseMessage('');
    setError('');
  };

  const closeResponseModal = () => {
    if (!responding) {
      setShowResponseModal(null);
      setResponseMessage('');
      setError('');
    }
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp || !timestamp.toDate) return 'Unknown';
    
    const now = new Date();
    const then = timestamp.toDate();
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <div className="access-requests-panel">
        <div className="loading-state">⏳ Loading requests...</div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="access-requests-panel">
        <div className="panel-header">
          <h3>🙋 Access Requests</h3>
          {stats && (
            <span className="stats-badge">
              {stats.total} total
            </span>
          )}
        </div>
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <p>No pending access requests</p>
          {stats && stats.total > 0 && (
            <p className="stats-text">
              You've responded to {stats.approved + stats.denied} requests 
              ({stats.approvalRate}% approval rate)
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="access-requests-panel">
      <div className="panel-header">
        <h3>🙋 Access Requests ({requests.length})</h3>
        {stats && (
          <span className="stats-badge">
            {stats.approvalRate}% approval rate
          </span>
        )}
      </div>

      {error && (
        <div className="error-banner">
          ❌ {error}
        </div>
      )}

      <div className="requests-list">
        {requests.map(request => (
          <div key={request.id} className="request-card">
            {/* Requester Info */}
            <div className="request-header">
              <div className="requester-info">
                <strong className="requester-name">
                  {request.requesterName}
                </strong>
                <span className="requester-email">{request.requesterEmail}</span>
              </div>
              <span className="request-time">{getTimeAgo(request.createdAt)}</span>
            </div>

            {/* Request Details */}
            <div className="request-body">
              <div className="request-meta">
                <span className="meta-item">
                  <strong>Reason:</strong> {request.reason}
                </span>
                <span className="meta-item">
                  <strong>Job:</strong> {request.jobTitle}
                </span>
              </div>

              <div className="request-message">
                <strong>Message:</strong>
                <p>{request.message}</p>
              </div>

              <div className="request-ids">
                <small>Job ID: {request.jobId}</small>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="request-actions">
              <button
                onClick={() => openResponseModal(request, 'approve')}
                disabled={responding === request.id}
                className="approve-btn"
              >
                ✅ Approve
              </button>
              <button
                onClick={() => openResponseModal(request, 'deny')}
                disabled={responding === request.id}
                className="deny-btn"
              >
                ❌ Deny
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Response Modal */}
      {showResponseModal && (
        <div className="modal-overlay" onClick={closeResponseModal}>
          <div className="modal-content response-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {showResponseModal.action === 'approve' ? '✅ Approve Request' : '❌ Deny Request'}
              </h3>
              <button 
                className="close-btn"
                onClick={closeResponseModal}
                disabled={responding}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="confirm-info">
                <p><strong>From:</strong> {showResponseModal.request.requesterName}</p>
                <p><strong>Job:</strong> {showResponseModal.request.jobTitle}</p>
                <p><strong>Their message:</strong></p>
                <div className="quoted-message">
                  {showResponseModal.request.message}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="owner-response">
                  Your message (optional):
                </label>
                <textarea
                  id="owner-response"
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  placeholder={
                    showResponseModal.action === 'approve'
                      ? 'Add a message... (e.g., "Happy to help! Good luck with your case.")'
                      : 'Add a reason for denial... (e.g., "Sorry, this is part of an ongoing investigation.")'
                  }
                  rows={3}
                  maxLength={200}
                  disabled={responding}
                />
                <div className="char-count">{responseMessage.length}/200</div>
              </div>

              {showResponseModal.action === 'approve' && (
                <div className="info-box">
                  <strong>⚠️ Before approving:</strong>
                  <ul>
                    <li>User will get full access to this job and video</li>
                    <li>You can revoke access anytime</li>
                    <li>Access is free (no payment)</li>
                  </ul>
                </div>
              )}

              {error && (
                <div className="error-message">
                  ❌ {error}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                onClick={closeResponseModal}
                disabled={responding}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRespond(showResponseModal.request.id, showResponseModal.action)}
                disabled={responding}
                className={showResponseModal.action === 'approve' ? 'btn-approve' : 'btn-deny'}
              >
                {responding ? (
                  '⏳ Processing...'
                ) : (
                  showResponseModal.action === 'approve' ? '✅ Approve Request' : '❌ Deny Request'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessRequestsPanel;

